import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../utils/ThemeSwitcher';
import logoSource from '../assets/dataquadio.png';
import logoSourceInverted from '../assets/dataquadio_inverted.png';

// ============================================================================
// CONFIG
// ============================================================================
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PULSE_API = `${API_BASE}/api/pulse`;

const api = (path, token, opts = {}) =>
  fetch(`${PULSE_API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...opts.headers },
  }).then(async r => {
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || r.statusText);
    return r.json();
  });

// ============================================================================
// COLORS
// ============================================================================
const colors = (dark) => ({
  bg: dark ? '#0B0D11' : '#F7F8FA',
  card: dark ? '#13161D' : '#FFFFFF',
  cardAlt: dark ? '#191D27' : '#F1F3F6',
  border: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
  text: dark ? '#E8EAF0' : '#111827',
  textSec: dark ? '#8B92A5' : '#6B7280',
  textMuted: dark ? '#555C6E' : '#9CA3AF',
  accent: '#2B8AFF',
  green: '#10B981',
  amber: '#F59E0B',
  red: '#EF4444',
  purple: '#8B5CF6',
});

// Sun/Moon SVG toggle
const ThemeIcon = ({ dark, size = 16 }) => dark ? (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
) : (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
);

// ============================================================================
// LOGIN SCREEN
// ============================================================================
const LoginScreen = ({ onLogin, dark, c, toggleTheme }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await api('/login', null, {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      onLogin(data.token);
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: c.bg, fontFamily: "'Outfit', sans-serif", position: 'relative' }}>
      <button onClick={toggleTheme} style={{ position: 'absolute', top: 20, right: 20, padding: '8px', borderRadius: 8, border: `1px solid ${c.border}`, background: c.card, color: c.textSec, cursor: 'pointer', lineHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ThemeIcon dark={dark} />
      </button>
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 380, padding: 32, borderRadius: 16, background: c.card, border: `1px solid ${c.border}` }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: c.accent, marginBottom: 8 }}>Pulse</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <img src={dark ? logoSourceInverted : logoSource} alt="DataQuad" style={{ width: 26, height: 26 }} />
            <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: -0.5 }}>
              <span style={{ color: c.text }}>data</span><span style={{ color: c.accent }}>Quad</span>
            </span>
            <span style={{ fontSize: 14, color: c.textSec, fontWeight: 500 }}>Monitor</span>
          </div>
        </div>
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" required
          style={{ width: '100%', padding: '12px 14px', marginBottom: 12, borderRadius: 10, border: `1px solid ${c.border}`, background: c.cardAlt, color: c.text, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
        <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" type="password" required
          style={{ width: '100%', padding: '12px 14px', marginBottom: 16, borderRadius: 10, border: `1px solid ${c.border}`, background: c.cardAlt, color: c.text, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
        {error && <div style={{ color: c.red, fontSize: 13, marginBottom: 12, textAlign: 'center' }}>{error}</div>}
        <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: c.accent, color: '#FFF', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </div>
  );
};

// ============================================================================
// STAT CARD
// ============================================================================
const Stat = ({ label, value, sub, color, c }) => (
  <div style={{ padding: '20px 22px', borderRadius: 14, background: c.card, border: `1px solid ${c.border}` }}>
    <div style={{ fontSize: 13, color: c.textSec, fontWeight: 500, marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 32, fontWeight: 700, color: color || c.text, letterSpacing: -1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: c.textMuted, marginTop: 4 }}>{sub}</div>}
  </div>
);

// ============================================================================
// MINI BAR CHART (signup timeline)
// ============================================================================
const SignupChart = ({ data, c }) => {
  if (!data || !data.length) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ padding: '20px 22px', borderRadius: 14, background: c.card, border: `1px solid ${c.border}` }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: c.text, marginBottom: 16 }}>Signups (last 30 days)</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80 }}>
        {data.map((d, i) => (
          <div key={i} title={`${d.date}: ${d.count}`} style={{
            flex: 1, minWidth: 0, borderRadius: '3px 3px 0 0',
            background: d.count > 0 ? c.accent : (c.border),
            height: `${Math.max((d.count / max) * 100, 4)}%`,
            opacity: d.count > 0 ? 1 : 0.3,
            transition: 'height 0.3s ease',
          }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span style={{ fontSize: 10, color: c.textMuted }}>{data[0]?.date?.slice(5)}</span>
        <span style={{ fontSize: 10, color: c.textMuted }}>{data[data.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
};

// ============================================================================
// USER TABLE
// ============================================================================
const UserTable = ({ users, c }) => {
  const [search, setSearch] = useState('');
  const filtered = users.filter(u =>
    (u.email + (u.name || '')).toLowerCase().includes(search.toLowerCase())
  );

  const ago = (iso) => {
    if (!iso) return '—';
    const d = (Date.now() - new Date(iso).getTime()) / 1000;
    if (d < 60) return 'just now';
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  const statusBadge = (s) => {
    const map = { active: c.green, trialing: c.amber, none: c.textMuted, canceled: c.red, past_due: c.red };
    return (
      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: `${map[s] || c.textMuted}18`, color: map[s] || c.textMuted }}>
        {s || 'none'}
      </span>
    );
  };

  return (
    <div style={{ borderRadius: 14, background: c.card, border: `1px solid ${c.border}`, overflow: 'hidden' }}>
      <div style={{ padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${c.border}` }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: c.text }}>Users ({users.length})</div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
          style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${c.border}`, background: c.cardAlt, color: c.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 200 }} />
      </div>
      <div style={{ overflowX: 'auto', maxHeight: 520 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: c.cardAlt }}>
              {['User', 'Auth', 'Subscription', 'Projects', 'With Data', 'Signed Up', 'Last Login'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: c.textSec, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} style={{ borderTop: `1px solid ${c.border}` }}>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {u.picture ? (
                      <img src={u.picture} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
                    ) : (
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: c.accent + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: c.accent }}>
                        {(u.name || u.email)[0].toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div style={{ fontWeight: 600, color: c.text, whiteSpace: 'nowrap' }}>{u.name || '—'}</div>
                      <div style={{ fontSize: 11, color: c.textMuted }}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '10px 14px', color: c.textSec }}>{u.is_google_auth ? 'Google' : 'Email'}</td>
                <td style={{ padding: '10px 14px' }}>{statusBadge(u.subscription_status)}</td>
                <td style={{ padding: '10px 14px', color: c.text, fontWeight: 600 }}>{u.project_count}</td>
                <td style={{ padding: '10px 14px', color: u.projects_with_data > 0 ? c.green : c.textMuted, fontWeight: 600 }}>{u.projects_with_data}</td>
                <td style={{ padding: '10px 14px', color: c.textSec, whiteSpace: 'nowrap' }}>{ago(u.created_at)}</td>
                <td style={{ padding: '10px 14px', color: c.textSec, whiteSpace: 'nowrap' }}>{ago(u.last_login_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// PROJECTS TABLE
// ============================================================================
const ProjectTable = ({ projects, c }) => {
  const [search, setSearch] = useState('');
  const filtered = projects.filter(p =>
    (p.name + p.owner_email + (p.owner_name || '')).toLowerCase().includes(search.toLowerCase())
  );

  const stageColor = (s) => {
    const map = { created: c.textMuted, data_loaded: c.amber, cleaning_in_progress: c.purple, dashboard: c.accent, completed: c.green };
    return map[s] || c.textSec;
  };

  return (
    <div style={{ borderRadius: 14, background: c.card, border: `1px solid ${c.border}`, overflow: 'hidden' }}>
      <div style={{ padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${c.border}` }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: c.text }}>Projects ({projects.length})</div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
          style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${c.border}`, background: c.cardAlt, color: c.text, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 200 }} />
      </div>
      <div style={{ overflowX: 'auto', maxHeight: 520 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: c.cardAlt }}>
              {['Project', 'Owner', 'Status', 'Stage', 'Data', 'Quality', 'Created', 'Last Active'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: c.textSec, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} style={{ borderTop: `1px solid ${c.border}`, opacity: p.deleted_at ? 0.5 : 1 }}>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ fontWeight: 600, color: c.text }}>{p.name}</div>
                  {p.description && <div style={{ fontSize: 11, color: c.textMuted, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description}</div>}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ fontSize: 12, color: c.textSec }}>{p.owner_name || p.owner_email}</div>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {p.deleted_at ? (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: `${c.red}18`, color: c.red }}>Closed</span>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: `${c.green}18`, color: c.green }}>{p.status}</span>
                  )}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: stageColor(p.workflow_stage) }}>{p.workflow_stage}</span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <span title="Primary" style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: p.has_primary_data ? `${c.green}18` : `${c.textMuted}12`, color: p.has_primary_data ? c.green : c.textMuted }}>P</span>
                    <span title="Backcheck" style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: p.has_backcheck_data ? `${c.accent}18` : `${c.textMuted}12`, color: p.has_backcheck_data ? c.accent : c.textMuted }}>BC</span>
                  </div>
                </td>
                <td style={{ padding: '10px 14px', fontWeight: 600, color: p.raw_quality_score ? c.text : c.textMuted }}>
                  {p.raw_quality_score != null ? `${p.raw_quality_score}%` : '—'}
                </td>
                <td style={{ padding: '10px 14px', color: c.textSec, whiteSpace: 'nowrap', fontSize: 12 }}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                <td style={{ padding: '10px 14px', color: c.textSec, whiteSpace: 'nowrap', fontSize: 12 }}>{p.last_accessed_at ? new Date(p.last_accessed_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN PULSE COMPONENT
// ============================================================================
const Pulse = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const c = colors(isDarkMode);

  const [token, setToken] = useState(() => sessionStorage.getItem('pulse_token'));
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [signups, setSignups] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = (t) => {
    sessionStorage.setItem('pulse_token', t);
    setToken(t);
  };

  const logout = () => {
    sessionStorage.removeItem('pulse_token');
    setToken(null);
  };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [ov, us, pr, su, fb] = await Promise.all([
        api('/overview', token),
        api('/users', token),
        api('/projects', token),
        api('/signups?days=30', token),
        api('/feedback', token),
      ]);
      setOverview(ov);
      setUsers(us.users);
      setProjects(pr.projects);
      setSignups(su.signups);
      setFeedback(fb.feedback);
    } catch (err) {
      if (err.message?.includes('401') || err.message?.includes('expired')) {
        logout();
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!token) return <LoginScreen onLogin={handleLogin} dark={isDarkMode} c={c} toggleTheme={toggleTheme} />;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'projects', label: 'Projects' },
    { id: 'feedback', label: 'Feedback' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: c.bg, fontFamily: "'Outfit', sans-serif", color: c.text }}>
      {/* Header */}
      <div style={{ borderBottom: `1px solid ${c.border}`, background: c.card }}>
        {/* Top row: logo + actions */}
        <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={isDarkMode ? logoSourceInverted : logoSource} alt="DataQuad" style={{ width: 24, height: 24 }} />
            <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: -0.3 }}>
              <span style={{ color: c.text }}>data</span><span style={{ color: c.accent }}>Quad</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: c.accent, marginLeft: 6 }}>Pulse</span>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={toggleTheme} style={{ padding: 7, borderRadius: 8, border: `1px solid ${c.border}`, background: 'transparent', color: c.textSec, cursor: 'pointer', lineHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ThemeIcon dark={isDarkMode} />
            </button>
            <button onClick={load} disabled={loading} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${c.border}`, background: 'transparent', color: c.textSec, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              {loading ? '...' : 'Refresh'}
            </button>
            <button onClick={logout} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${c.border}`, background: 'transparent', color: c.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              Sign out
            </button>
          </div>
        </div>
        {/* Tab row: scrollable */}
        <div style={{ padding: '0 20px 10px', display: 'flex', gap: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '6px 16px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
              background: tab === t.id ? `${c.accent}18` : 'transparent',
              color: tab === t.id ? c.accent : c.textSec,
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {error && <div style={{ padding: '12px 28px', color: c.red, fontSize: 13 }}>{error}</div>}

      <div style={{ padding: '20px 16px', maxWidth: 1200, margin: '0 auto' }}>
        {/* OVERVIEW TAB */}
        {tab === 'overview' && overview && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
              <Stat label="Total Users" value={overview.total_users} sub={`+${overview.users_7d} this week`} color={c.accent} c={c} />
              <Stat label="Total Projects" value={overview.total_projects} sub={`${overview.projects_with_data} with data`} c={c} />
              <Stat label="Active Subscriptions" value={overview.active_subscriptions} color={c.green} c={c} />
              <Stat label="Google Auth" value={overview.google_auth_users} sub={`${overview.email_auth_users} email`} c={c} />
              <Stat label="Closed Projects" value={overview.closed_projects} color={c.amber} c={c} />
              <Stat label="Deactivated" value={overview.deactivated_accounts} color={overview.deactivated_accounts > 0 ? c.red : c.textMuted} c={c} />
            </div>
            <SignupChart data={signups} c={c} />
            <div style={{ marginTop: 20 }}>
              <UserTable users={users} c={c} />
            </div>
          </>
        )}

        {/* USERS TAB */}
        {tab === 'users' && <UserTable users={users} c={c} />}

        {/* PROJECTS TAB */}
        {tab === 'projects' && <ProjectTable projects={projects} c={c} />}

        {/* FEEDBACK TAB */}
        {tab === 'feedback' && (
          <div style={{ borderRadius: 14, background: c.card, border: `1px solid ${c.border}`, overflow: 'hidden' }}>
            <div style={{ padding: '16px 22px', borderBottom: `1px solid ${c.border}` }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: c.text }}>Feedback ({feedback.length})</div>
            </div>
            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              {feedback.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: c.textMuted }}>No feedback yet</div>}
              {feedback.map(f => (
                <div key={f.id} style={{ padding: '14px 22px', borderTop: `1px solid ${c.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                        background: f.feedback_type === 'bug' ? `${c.red}18` : f.feedback_type === 'suggestion' ? `${c.amber}18` : `${c.green}18`,
                        color: f.feedback_type === 'bug' ? c.red : f.feedback_type === 'suggestion' ? c.amber : c.green,
                      }}>{f.feedback_type}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{f.user_name || f.user_email}</span>
                    </div>
                    <span style={{ fontSize: 11, color: c.textMuted }}>{f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}</span>
                  </div>
                  {f.subject && <div style={{ fontSize: 13, fontWeight: 600, color: c.text, marginBottom: 4 }}>{f.subject}</div>}
                  <div style={{ fontSize: 13, color: c.textSec, lineHeight: 1.5 }}>{f.description}</div>
                  {f.current_view && <div style={{ fontSize: 11, color: c.textMuted, marginTop: 4 }}>View: {f.current_view}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pulse;